import { useState, useEffect, useRef } from 'react';
import { Modal, ModalBody, Button, Text, Stack } from '@7shifts/sous-chef-base';
import { useTranslation } from '@src/i18n';
import { formatLocalTime } from './timeUtils';
import './OfflinePunchRecordedModal.css';

const DEFAULT_AUTO_DISMISS_MS = 5000;

interface OfflinePunchRecordedModalProps {
    punchTime: Date;
    locationTimezone: string;
    onDismiss: () => void;
    autoDismissMs?: number;
}

/**
 * "Offline punch recorded" confirmation (API-4648), shown immediately after
 * `handleOfflinePunch`/`completeOfflinePunch` (API-4650) stores a punch to
 * IndexedDB while offline. Mirrors `PunchResultModal`'s auto-dismiss
 * countdown pattern, per the iOS `OfflinePunchRecordedDialog` behaviour.
 */
export function OfflinePunchRecordedModal({
    punchTime,
    locationTimezone,
    onDismiss,
    autoDismissMs = DEFAULT_AUTO_DISMISS_MS
}: OfflinePunchRecordedModalProps) {
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;
    const { t } = useTranslation();

    const [remainingMs, setRemainingMs] = useState(autoDismissMs);

    useEffect(() => {
        const start = Date.now();
        const id = setInterval(() => {
            const left = Math.max(0, autoDismissMs - (Date.now() - start));
            setRemainingMs(left);
            if (left <= 0) {
                clearInterval(id);
                onDismissRef.current();
            }
        }, 1000);
        return () => clearInterval(id);
    }, [autoDismissMs]);

    const secs = Math.ceil(remainingMs / 1000);
    const countdown = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(
        2,
        '0'
    )}`;

    return (
        <Modal testId="offline-punch-recorded-modal">
            <ModalBody>
                <Stack space={20}>
                    <Stack space={4} alignItems="center">
                        <Text as="h1" alignment="center">
                            {t('offlinePunchRecorded.title')}
                        </Text>
                        <Text
                            as="h4"
                            alignment="center"
                            color="var(--color-surface-on-color-subtle)"
                        >
                            {t('offlinePunchRecorded.subtitle')}
                        </Text>
                    </Stack>
                    <div
                        className="offline-punch-recorded-modal__time"
                        data-testid="offline-punch-recorded-time"
                    >
                        <Text as="insight" alignment="center">
                            {formatLocalTime(
                                punchTime.toISOString(),
                                locationTimezone
                            )}
                        </Text>
                    </div>
                    <Button
                        type="button"
                        theme="primary"
                        size="full-width"
                        onClick={onDismiss}
                        testId="offline-punch-recorded-dismiss"
                    >
                        {t('offlinePunchRecorded.ok', { countdown })}
                    </Button>
                </Stack>
            </ModalBody>
        </Modal>
    );
}
